// La séance en cours, côté téléphone : version choisie, chrono, tours faits, ressenti.
// Elle survit à la fermeture de l'app (stockage local) jusqu'à « Valider ». Logique pure + persistance.

import type { Bloc, SeanceDuJour } from '@alfred/shared';

export type Version = 'complete' | 'courte';
export type Ressenti = 'facile' | 'correct' | 'dur';

export interface EtatQuete {
  /** Créneau et lettre : une quête d'un autre jour est ignorée. */
  date: string;
  lettre: string;
  version: Version;
  /** Horodatage (ms) de l'acceptation : il donne la durée. Null tant que rien n'a commencé. */
  debut: number | null;
  /** Tours faits par bloc (un intervalle terminé compte 1). */
  tours: number[];
  ressenti: Ressenti | null;
}

/** Les blocs réellement à faire : la version courte garde N exercices du premier circuit, en M tours. */
export function blocsDe(s: SeanceDuJour, version: Version, courte: { tours: number; nbExercices: number } | null): Bloc[] {
  if (version === 'complete' || !courte) return s.seance.blocs;
  const circuit = s.seance.blocs.find((b) => b.type === 'circuit');
  if (!circuit) return s.seance.blocs.slice(0, 1);
  return [{ ...circuit, tours: courte.tours, exercices: circuit.exercices.slice(0, courte.nbExercices) }];
}

export function toursPrevus(b: Bloc): number {
  return b.type === 'circuit' ? b.tours : 1;
}

export function nouvelleQuete(s: SeanceDuJour, version: Version, maintenant: number | null): EtatQuete {
  return { date: s.date, lettre: s.lettre, version, debut: maintenant, tours: [], ressenti: null };
}

/** Un tap sur « tour terminé » : +1, plafonné. Jamais de retour silencieux à zéro. */
export function ajouterTour(q: EtatQuete, bloc: number, max: number, maintenant: number): EtatQuete {
  const tours = [...q.tours];
  tours[bloc] = Math.min(max, (tours[bloc] ?? 0) + 1);
  return { ...q, tours, debut: q.debut ?? maintenant };
}

export function retirerTour(q: EtatQuete, bloc: number): EtatQuete {
  const tours = [...q.tours];
  tours[bloc] = Math.max(0, (tours[bloc] ?? 0) - 1);
  return { ...q, tours };
}

export function totaux(q: EtatQuete, blocs: Bloc[]): { faits: number; prevus: number } {
  return {
    faits: blocs.reduce((t, b, i) => t + Math.min(toursPrevus(b), q.tours[i] ?? 0), 0),
    prevus: blocs.reduce((t, b) => t + toursPrevus(b), 0),
  };
}

/** Minutes écoulées depuis l'acceptation, bornées ; null sans chrono (le daemon prendra la durée du programme). */
export function dureeMin(q: EtatQuete, maintenant: number): number | null {
  if (q.debut === null) return null;
  return Math.min(240, Math.max(1, Math.round((maintenant - q.debut) / 60_000)));
}

const CLE = 'brain.quete';

export function chargerQuete(s: SeanceDuJour | null): EtatQuete | null {
  if (!s) return null;
  try {
    const q = JSON.parse(localStorage.getItem(CLE) ?? 'null') as EtatQuete | null;
    return q && q.date === s.date && q.lettre === s.lettre ? q : null;
  } catch {
    return null;
  }
}

export function sauverQuete(q: EtatQuete | null): void {
  try {
    if (q) localStorage.setItem(CLE, JSON.stringify(q));
    else localStorage.removeItem(CLE);
  } catch {
    /* stockage indisponible : la séance ne survivra pas à une fermeture */
  }
}
